import { INSTALL_SUPPORT, readInstallSupport } from '../../domain/pwa/installOffer';
import { writeInstallCompleted } from './offerPreferences';

/**
 * The browser side of installing: catching the prompt, and firing it.
 *
 * Chromium fires `beforeinstallprompt` ONCE, early, and usually before React has
 * mounted anything that could listen for it. Miss it and there is no way to ask
 * for it again — so the listener is bound from `bootstrap.js` at start-up and
 * the event is parked here, at module scope, for whatever mounts later. That is
 * the whole reason this is not a hook.
 *
 * `preventDefault()` on the event is what stops Chromium showing its own mini
 * infobar, which is the difference between the app choosing its moment and the
 * browser choosing one for it.
 */

let deferredPrompt = null;
let hasBoundListeners = false;

const listeners = new Set();

function notify() {
    listeners.forEach((listener) => {
        listener(readInstallPromptState());
    });
}

export function readStandaloneDisplayMode(windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject) {
        return false;
    }

    if (typeof windowObject.matchMedia === 'function') {
        try {
            if (windowObject.matchMedia('(display-mode: standalone)').matches) {
                return true;
            }
        } catch (error) {
            // Safari has thrown on unsupported display-mode queries; fall
            // through to the iOS-only flag below rather than taking the shell
            // down.
        }
    }

    // iOS Safari's own, and the only signal it gives for a home-screen launch.
    return Boolean(windowObject.navigator && windowObject.navigator.standalone === true);
}

export function readInstallPromptState(windowObject = typeof window === 'undefined' ? null : window) {
    if (!windowObject || !windowObject.navigator) {
        return { support: INSTALL_SUPPORT.unsupported, isStandalone: false };
    }

    const isStandalone = readStandaloneDisplayMode(windowObject);

    return {
        isStandalone,
        support: readInstallSupport({
            userAgent: windowObject.navigator.userAgent || '',
            platform: windowObject.navigator.platform || '',
            maxTouchPoints: windowObject.navigator.maxTouchPoints || 0,
            isStandalone,
            hasDeferredPrompt: Boolean(deferredPrompt),
        }),
    };
}

/**
 * Bound once, from bootstrap, before anything renders.
 *
 * `appinstalled` matters as much as the prompt event: without it the card stays
 * on screen after the app has been installed from the browser's own menu, which
 * is the one moment it is provably useless.
 */
export function startInstallPromptWatch(windowObject = typeof window === 'undefined' ? null : window) {
    if (hasBoundListeners || !windowObject || typeof windowObject.addEventListener !== 'function') {
        return;
    }

    hasBoundListeners = true;

    windowObject.addEventListener('beforeinstallprompt', (event) => {
        if (event && typeof event.preventDefault === 'function') {
            event.preventDefault();
        }

        deferredPrompt = event;
        notify();
    });

    windowObject.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        // Recorded here, from a listener bound in `bootstrap.js`, because this
        // can fire while the offer card is unmounted — the reader may be on a
        // car detail page, or have installed from the browser's own menu.
        writeInstallCompleted(Date.now());
        notify();
    });
}

export function subscribeInstallPromptState(listener) {
    listeners.add(listener);
    listener(readInstallPromptState());

    return () => {
        listeners.delete(listener);
    };
}

/**
 * Fire the browser's own install dialog.
 *
 * Resolves to the outcome, or `unavailable` where there was never a prompt to
 * fire — every non-Chromium path lands there, and the caller shows the
 * platform's instruction instead. The event is consumed either way: it cannot be
 * fired twice, and keeping it would leave a button that silently does nothing.
 */
export async function promptForInstall() {
    const prompt = deferredPrompt;

    if (!prompt || typeof prompt.prompt !== 'function') {
        return { outcome: 'unavailable' };
    }

    deferredPrompt = null;

    try {
        await prompt.prompt();

        const choice = await prompt.userChoice;
        const outcome = choice && choice.outcome === 'accepted' ? 'accepted' : 'dismissed';

        // Belt and braces: `appinstalled` records this too, but engines have
        // differed on whether it fires at all after a prompt accepted in-page.
        if (outcome === 'accepted') {
            writeInstallCompleted(Date.now());
        }

        notify();

        return { outcome };
    } catch (error) {
        notify();

        return { outcome: 'failed' };
    }
}

/** Test seam — module scope would otherwise carry one spec's prompt into the next. */
export function resetInstallPromptWatch() {
    deferredPrompt = null;
    hasBoundListeners = false;
    listeners.clear();
}
